// @react
import React, { ReactElement, useEffect, useState, useContext } from 'react'
// @types
import {
  UserUpsertMutation,
  UserUpsertMutationVariables,
  GetUserQueryResult,
  GetEmailValidatorQuery,
  GetEmailValidatorQueryVariables,
  GetUserQuery,
} from 'lib/graphql/graphqlTypes'
import { SnackbarTypes } from '@bit/atd.web.snackbar'
import { CreateAccountFormValues } from 'modules/common/forms/types/createAccount'
// @common
import { SnackbarContext } from 'modules/common/context/snackbar'
import { LoginMutation, LoginMutationVariables } from 'lib/graphql/graphqlTypes'
// @graphQL
import { ApolloQueryResult, useMutation, useQuery } from '@apollo/client'
import { UPSERT_USER } from 'graphql/mutations/user.mutation'
// @libraries
import { useHistory, useLocation } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { DevTool } from '@hookform/devtools'
import { yupResolver } from '@hookform/resolvers/yup'
import { makeStyles } from '@material-ui/core/styles'
import { vsprintf } from 'sprintf-js'
import strings from 'lib/constants/strings'
import countryISOCode from 'i18n-iso-countries'
// @components
import Container from '@bit/atd.web.container'
import Grid from '@bit/atd.web.grid'
import Modal from '@bit/atd.web.modal'
import Spacer from '@bit/atd.web.spacer'
import Button from '@bit/atd.web.button'
import Box from '@bit/atd.web.box'
import ConditionalLoader from '@bit/atd.web.loaders.conditional-loader'
import SkeletonLoader from '@bit/atd.web.loaders.skeleton'
// @module components
import AccountFields from 'modules/common/forms/fields/createAccount/Account'
import ProfessionalFields from 'modules/common/forms/fields/createAccount/Professional'
import ContactFields from 'modules/common/forms/fields/createAccount/Contact'
import AcceptTermsFields from 'modules/common/forms/fields/createAccount/AcceptTerms'
import { validationSchema } from 'modules/common/forms/validations/createAccount'
import Helper from 'utils/Helper.class'
import * as Yup from 'yup'
import useDebounce from 'modules/common/hooks/useDebounce'
import { EMAIL_VALIDATOR } from 'graphql/queries/user.query'
import { LOGIN } from 'graphql/mutations/auth.mutation'
import { doLogin } from 'modules/auth/helpers'

/**
 * OnBoarding - Create Account
 * https://www.figma.com/file/qfo4N93iI12FDinKJQaXKH/ATD-MyATD-Desktop-2020?node-id=174%3A47986
 */

const useStyles = makeStyles(() => ({
  container: {
    maxWidth: '768px',
    margin: '0 auto',
    '& .MuiPaper-root': {
      width: '100%',
    },
  },
  formContainer: {
    position: 'relative',
  },
  ActionButtons: {
    display: 'flex',
    '& button:last-child': {
      marginLeft: '12px',
    },
  },
}))

type PropsType = {
  data?: GetUserQueryResult['data']
  isLoading: boolean
  refetch: () => Promise<ApolloQueryResult<GetUserQuery>>
}

const CreateAccount = ({
  data: userData,
  isLoading,
  refetch,
}: PropsType): ReactElement => {
  /******************************************
   * STATE
   ******************************************/

  const [showModal, setShowModal] = useState<boolean>(false)
  const [isMutationSubmitting, setIsSubmitting] = useState<boolean>(false)
  const [validatorEmail, setValidatorEmail] = useState('')
  const [validatorEmailMessage, setValidatorEmailMessage] = useState<string>('')

  /******************************************
   * HOOKS
   ******************************************/

  const [login] = useMutation<LoginMutation, LoginMutationVariables>(LOGIN)

  const snackbar = useContext(SnackbarContext)
  const location = useLocation()
  const history = useHistory()
  const classes = useStyles()

  /******************************************
   * GQL HOOKS
   ******************************************/

  // Mutation
  const [upsertUser] = useMutation<
    UserUpsertMutation,
    UserUpsertMutationVariables
  >(UPSERT_USER)

  /******************************************
   * HOOKS
   ******************************************/

  // Query
  const { data: emailValidatorData } = useQuery<
    GetEmailValidatorQuery,
    GetEmailValidatorQueryVariables
  >(EMAIL_VALIDATOR, {
    variables: {
      input: { email: validatorEmail },
    },
    fetchPolicy: 'network-only',
    skip: !validatorEmail || !!userData?.user?.id,
  })
  const emailValidator = emailValidatorData?.emailValidator

  /**
   * add state based validator to primaryEmail
   */
  const primaryEmailAddress = Yup.string().test(
    'Email Validator',
    validatorEmailMessage,
    function () {
      if (userData?.user?.id) {
        return true
      }
      return !!emailValidatorData?.emailValidator.isValid
    },
  )

  /**
   * create second schema
   */
  const moreValidationSchema = Yup.object().shape({
    primaryEmailAddress,
  })

  /**
   * merge schemas together
   */
  const mergedSchema = Helper.merge(validationSchema, moreValidationSchema)

  // React Hook Form
  const formMethods = useForm<CreateAccountFormValues>({
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    resolver: yupResolver(mergedSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      primaryEmailAddress: '',
      password: '',
      emailValidator: true,
      passwordRetype: '',
      industry: '',
      role: '',
      addressType: '',
      countryCode: '',
      address1: '',
      address2: '',
      address3: '',
      city: '',
      postalCode: '',
      state: '',
      isPostalMailOptedOut: false,
      acceptTerms: false,
      acceptGDPR1: false,
      acceptGDPR2: false,
    },
  })
  const { control, formState, reset, setError, clearErrors } = formMethods
  const { errors } = formState
  const email = formMethods.watch('primaryEmailAddress')

  const debouncedEmail = useDebounce(email, 100)
  const user = userData?.user

  useEffect(() => {
    if (emailValidatorData && !emailValidator?.isValid) {
      if (emailValidator?.message) {
        setValidatorEmailMessage(emailValidator.message)
        setError('primaryEmailAddress', {
          type: 'manual',
          message: emailValidator.message,
        })
      } else {
        setValidatorEmailMessage('Email address is not valid')
        setError('primaryEmailAddress', {
          type: 'manual',
          message: 'Email address is not valid',
        })
      }
    }
    if (emailValidatorData && emailValidator?.isValid) {
      clearErrors('primaryEmailAddress')
    }
  }, [emailValidator, setError, clearErrors, emailValidatorData])

  useEffect(() => {
    if (email !== '') {
      setValidatorEmail(email)
    }
  }, [debouncedEmail])

  /**
   *
   */
  useEffect(() => {
    if (userData?.user?.id) {
      // @ts-ignore
      delete validationSchema.fields?.acceptGDPR1
      // @ts-ignore
      delete validationSchema.fields?.acceptGDPR2
      // @ts-ignore
      delete validationSchema.fields?.acceptTerms
      // @ts-ignore
      delete validationSchema.fields?.passwordRetype
      // @ts-ignore
      delete validationSchema.fields?.password
      // @ts-ignore
      delete validationSchema.fields?.firstName
      // @ts-ignore
      delete validationSchema.fields?.lastName
      // @ts-ignore
      delete validationSchema.fields?.primaryEmailAddress
    }

    if (userData?.user) {
      const userAddress = userData?.user?.contactInfo.address?.find(
        (item) => item.isPrimary,
      )

      reset({
        firstName: userData?.user?.profile.firstName || '',
        lastName: userData?.user?.profile.lastName || '',
        primaryEmailAddress:
          userData?.user?.contactInfo.primaryEmailAddress || '',
        password: '',
        passwordRetype: '',
        industry: userData?.user?.job.jobIndustry || '',
        role: userData?.user?.job.jobFunctionCode || '',
        addressType: userAddress?.addressType || '',
        // companyName: userData?.user?.job.companyName || '',
        countryCode:
          (userAddress?.countryCode &&
            countryISOCode.alpha3ToAlpha2(userAddress?.countryCode)) ||
          '',
        address1: userAddress?.address1 || '',
        address2: userAddress?.address2 || '',
        address3: userAddress?.address3 || '',
        city: userAddress?.city || '',
        postalCode: userAddress?.postalCode || '',
        state: userAddress?.state || '',
        isPostalMailOptedOut:
          userData?.user?.communicationPreferences.isPostalMailOptedOut ||
          false,
        acceptTerms: !!userData?.user,
        acceptGDPR1: !!userData?.user,
        acceptGDPR2: !!userData?.user,
      })
    }
  }, [reset, user, location])

  /******************************************
   * FUNCTIONS
   ******************************************/

  // on FormSubmit
  const handleFormSubmit = async (data: CreateAccountFormValues) => {
    const useraddressId =
      (userData &&
        userData.user?.contactInfo.address?.find((item) => {
          return item.isPrimary
        })?.id) ||
      null

    const { errors: mutationErrors } = await upsertUser({
      variables: {
        input: {
          firstName: data.firstName,
          lastName: data.lastName,
          password: data.password,
          primaryEmailAddress: data.primaryEmailAddress,
          jobFunctionCode: data.role,
          addressId: useraddressId,
          userIndustry: data.industry,
          addressType: data.addressType,
          countryCode: countryISOCode.alpha2ToAlpha3(data.countryCode),
          address1: data.address1,
          address2: data.address2,
          // address3: data.address3,
          city: data.city,
          postalCode: data.postalCode,
          state: data.state,
          isPostalMailOptedOut: data.isPostalMailOptedOut || false,
        },
      },
    })

    if (!mutationErrors) {
      snackbar.setMessage({
        type: SnackbarTypes.SUCCESS,
        message: vsprintf(strings.SUCCESS.GENERIC_VARIABLE_UPDATE_SUCCESS, [
          'Account',
        ]),
        show: true,
      })

      const loginRes = await doLogin({
        data: {
          username: data.primaryEmailAddress,
          password: data.password,
        },
        login,
      })
      // @todo i don't know if this logic need revising for newly created users
      await refetch()
      setShowModal(true)
    } else {
      snackbar.setMessage({
        type: SnackbarTypes.ERROR,
        message: strings.ERRORS.GENERIC_UPDATE_ERROR,
        show: true,
      })
      console.log('mutation error', mutationErrors)
    }
  }

  /******************************************
   * RENDER
   ******************************************/

  const modal = (
    <Modal
      headerNoSeparator
      forceAction
      footerNoSeparator
      open={showModal}
      size="small"
      title={strings.ONBOARDING.CREATE_ACCOUNT.MODAL.WELCOME}
      actions={
        <div className={classes.ActionButtons}>
          <Button
            variant="tertiary"
            size="medium"
            onClick={() => {
              setShowModal(false)
              history.push(Helper.getPathFromRouteKey('MYATD_DASHBOARD'))
            }}
          >
            {strings.ONBOARDING.CREATE_ACCOUNT.MODAL.SKIP}
          </Button>

          <Button
            variant="primary"
            size="medium"
            onClick={() => {
              setShowModal(false)
              history.push('/onboarding/step/1')
            }}
          >
            {strings.ONBOARDING.CREATE_ACCOUNT.MODAL.GET_STARTED}
          </Button>
        </div>
      }
    >
      <span>{strings.ONBOARDING.CREATE_ACCOUNT.NEXT_STEP}</span>
    </Modal>
  )

  const box = (
    content: ReactElement,
    isLoading: boolean,
    title?: string,
    subtitle?: string,
  ): ReactElement => {
    return title || subtitle ? (
      <Box
        header={{
          title: title,
          subtitle: subtitle,
        }}
      >
        {!isLoading ? content : <SkeletonLoader />}
      </Box>
    ) : (
      <Box>{!isLoading ? content : <SkeletonLoader />}</Box>
    )
  }

  const canSubmit = (): boolean => {
    return !(
      isMutationSubmitting ||
      (!userData?.user?.id && !emailValidatorData?.emailValidator.isValid)
    )
  }

  return (
    <>
      {process.env.DEBUG === '1' && <DevTool control={control} />}
      <Spacer size={32} fullWidth />

      {/* Form - RHF */}
      <form className={classes.formContainer}>
        <Container>
          <Grid className={classes.container} justify="center" container>
            {box(
              <AccountFields
                formMethods={formMethods}
                control={control}
                errors={errors}
                userData={userData}
              />,
              isLoading,
              'Account Information',
            )}

            <Spacer size={24} fullWidth />

            {box(
              <ProfessionalFields
                formMethods={formMethods}
                control={control}
                errors={errors}
                userData={userData}
              />,
              isLoading,
              'Professional Information',
              'Choose the options most applicable to you.',
            )}

            <Spacer size={24} fullWidth />

            {box(
              <ContactFields
                formMethods={formMethods}
                control={control}
                errors={errors}
              />,
              isLoading,
              'Contact Information',
              'Choose the options most applicable to you.',
            )}

            {!userData?.user && (
              <>
                <Spacer size={24} fullWidth />
                {box(
                  <AcceptTermsFields
                    formMethods={formMethods}
                    control={control}
                    errors={errors}
                  />,
                  isLoading,
                )}
              </>
            )}
          </Grid>
        </Container>

        <Spacer size={32} fullWidth />

        {/* Submit Button */}
        <Container>
          <Grid
            className={classes.container}
            container
            alignItems="center"
            justify="center"
          >
            <Grid item>
              <ConditionalLoader condition={!isMutationSubmitting}>
                <Button
                  disabled={!canSubmit()}
                  onClick={formMethods.handleSubmit(async (data) => {
                    setIsSubmitting(true)
                    await handleFormSubmit(data)
                    setIsSubmitting(false)
                  })}
                  size="medium"
                >
                  Save and Continue
                </Button>
              </ConditionalLoader>
            </Grid>
          </Grid>
        </Container>

        <Spacer size={32} fullWidth />
      </form>

      {/* Modal - Continues to step 1 */}
      {modal}
    </>
  )
}

export default CreateAccount
